import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEndpoints } from './api-endpoints';
import {
  FilteredTripsResponse,
  FilterSuggestionsResponse,
  FilterSuggestionsDto,
  TripFilters,
} from '../models/billing.model';

export interface InvoiceRequest {
  bills: Array<{
    billingId: string;
    amount: number;
    description: string;
  }>;
  totalAmount: number;
  paidTo: {
    vendorId: string;
    vendorName: string;
    bankDetails: {
      bankName: string;
      accountNumber: string;
      ifscCode: string;
      accountHolder: string;
    };
  };
  extras: Array<{
    type: 'DISCOUNT' | 'ADDON';
    description: string;
    amount: number;
  }>;
}

export interface InvoiceResponse {
  statusCode: number;
  message: string;
  data: Array<{
    invoiceId: string;
    bills: Array<{
      billingId: string;
      amount: number;
      description: string;
    }>;
    extras: Array<{
      type: 'DISCOUNT' | 'ADDON';
      description: string;
      amount: number;
    }>;
    totalAmount: number;
    paidTo: {
      vendorId: string;
      vendorName: string;
      bankDetails: {
        bankName: string;
        accountNumber: string;
        ifscCode: string;
        accountHolder: string;
      };
    };
    isPaid: boolean;
    status: string;
    createdAt: string;
    updatedAt: string;
    _id: string;
    __v: number;
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class BillingService {
  private baseUrl = environment.baseUrl;

  constructor(private http: HttpClient) {}

  getFilterSuggestions(
    filters?: FilterSuggestionsDto
  ): Observable<FilterSuggestionsResponse> {
    let params = new HttpParams();

    if (filters) {
      if (filters.type) params = params.set('type', filters.type);
      if (filters.vendorId) params = params.set('vendorId', filters.vendorId);
      if (filters.routeId) params = params.set('routeId', filters.routeId);
      if (filters.vehicleId)
        params = params.set('vehicleId', filters.vehicleId);
      if (filters.fleetType)
        params = params.set('fleetType', filters.fleetType);
      if (filters.driverId) params = params.set('driverId', filters.driverId);
    }

    const url = `${this.baseUrl}${ApiEndpoints.BILLING_GET_FILTER_SUGGESTIONS}`;

    return this.http.get<FilterSuggestionsResponse>(url, { params });
  }

  getFilteredTrips(filters: TripFilters): Observable<FilteredTripsResponse> {
    let params = new HttpParams();

    if (filters.type) params = params.set('type', filters.type);
    if (filters.routeId) params = params.set('routeId', filters.routeId);
    if (filters.vendorId) params = params.set('vendorId', filters.vendorId);
    if (filters.driverId) params = params.set('driverId', filters.driverId);
    if (filters.driverName)
      params = params.set('driverName', filters.driverName);
    if (filters.vehicleId) params = params.set('vehicleId', filters.vehicleId);
    if (filters.vehicleNumber)
      params = params.set('vehicleNumber', filters.vehicleNumber);
    if (filters.vehicleSize)
      params = params.set('vehicleSize', filters.vehicleSize);
    if (filters.dateRange) {
      params = params.set('startDate', filters.dateRange.startDate);
      params = params.set('endDate', filters.dateRange.endDate);
    }

    return this.http.get<FilteredTripsResponse>(
      `${this.baseUrl}${ApiEndpoints.BILLING_GET_FILTERED_TRIPS}`,
      { params }
    );
  }

  createInvoice(invoiceData: InvoiceRequest): Observable<InvoiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      authtype: 'jwt',
      jwt: this.getToken(),
      version: '1.0',
    });

    return this.http.post<InvoiceResponse>(
      `${this.baseUrl}/billing/invoice`,
      invoiceData,
      { headers }
    );
  }

  private getToken(): string {
    return localStorage.getItem('token') || '';
  }
}
